/**
 * Conferencing (VC detection) ladder for FC quality_mode=low (FE-driven).
 * Hermana de absenceLadder.ts: mismas reglas puras (constantes + una función
 * de decisión), sin timers ni TTS acá — eso lo orquesta fc-low-runtime.ts,
 * igual que tickAbsence()/speakAbsenceCheckin() hacen con absenceNextAction().
 *
 * Trigger: VcScorerService.state$ (nivel de score), NO silencio — ver
 * CONTRATO-VC-DETECTION.md §1/§2 y su ADENDA.
 */
import { VcLevel } from 'src/app/services/watchers/vc-signals.types';

/** Avisos hablados antes de suspender (§0: "Avisos antes de cerrar: 2"). */
export const VC_MAX_STRIKES = 2;
/** Gracia tras el AVISO 1 (§2/§3 default grace_seconds_first). */
export const VC_GRACE_FIRST_S = 30;
/** Gracia tras el AVISO 2 (§2/§3 default grace_seconds_second). */
export const VC_GRACE_SECOND_S = 60;
/**
 * Umbral del re-chequeo al expirar la gracia (§2: "si el score bajó de 30").
 * Redeclarado a propósito (no importado de vc-scorer.service.ts) — mismo
 * principio que absenceLadder.ts: este archivo es la fuente de verdad de LA
 * ESCALERA, tomada literal del contrato, no un espejo de internals privados
 * de otro archivo.
 */
export const VC_RECHECK_CLEAR_SCORE = 30;

/** Textos LITERALES del contrato §2 — tono neutro, nunca parafrasear. */
export const VC_WARNING_1_TEXT =
  'Detectamos señales de una aplicación de audio o video activa en tu equipo. Cerrala para continuar. Este evento queda registrado.';
export const VC_WARNING_2_TEXT =
  'Volvimos a detectar una herramienta de comunicación activa. Cerrá la aplicación para continuar. Si persiste, tu prueba se pausará y pasará a revisión.';
/** Fallback local si la llamada a fc-low-api falla — mismo texto que el BE
 *  devuelve normalmente (conferencingFarewell.ts, BE), para no dejar al
 *  candidato sin mensaje ante un corte de red justo al suspender. */
export const VC_SUSPENSION_FALLBACK_TEXT =
  'Tu prueba fue pausada por señales de un entorno no permitido y pasará a revisión por nuestro equipo. Te contactaremos.';

export type VcLadderAction = 'wait' | 'warn' | 'suspend';

/**
 * Escalera de conferencing: wait | warn | suspend.
 * - `confirmed` → suspend directo (sin más avisos), sin importar strikes.
 * - `suspect` con strikes ya agotados (3ª vez) → suspend.
 * - `suspect` con margen → warn (el runtime decide si es AVISO 1 o 2 mirando
 *   cuántos strikes ya tiene bancados).
 * - `watch`/`clear` → wait (el contador de strikes NO se resetea acá —
 *   "cleared NO resetea el contador de avisos", anti-gaming).
 */
export function vcLadderNextAction(level: VcLevel, strikesGiven: number): VcLadderAction {
  if (level === 'confirmed') return 'suspend';
  if (level !== 'suspect') return 'wait';
  return strikesGiven >= VC_MAX_STRIKES ? 'suspend' : 'warn';
}

/**
 * Re-chequeo dirigido al expirar la gracia de un aviso: si el score ya bajó
 * del umbral, el aviso NO cuenta strike (vuelve a watch en los hechos).
 */
export function vcRecheckClearsStrike(score: number): boolean {
  return score < VC_RECHECK_CLEAR_SCORE;
}

/** Texto + gracia por índice de strike YA bancado (0 → próximo es el 1er
 *  aviso, 1 → próximo es el 2do). Puro lookup, sin estado. */
export function vcWarningCopy(strikesGiven: number): { text: string; graceS: number } {
  return strikesGiven === 0
    ? { text: VC_WARNING_1_TEXT, graceS: VC_GRACE_FIRST_S }
    : { text: VC_WARNING_2_TEXT, graceS: VC_GRACE_SECOND_S };
}
